import { useCallback, useEffect, useMemo } from "react";
import { useDatasetPreviewLazyQuery } from "graphql/types/graphql";
import { useTable } from "react-table";
import { tableStyles } from "./dataPreviewStyle";
import {
  CircularProgress as LoadingIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
} from "@mui/material";
import { styled } from "@mui/system";
import colors from "assets/theme-dark/base/colors";
import Divider from "@mui/material/Divider";
import { EllipsisWithTooltip } from "../../headroom/components/EllipsisWithTooltip";

interface DatasetPreviewProps {
  cubeName: string;
  datasetId: string;
  datasetName: string;
  tableWidth?: string;
}

const DatasetPreviewComponent = ({ cubeName, datasetId, datasetName, tableWidth }: DatasetPreviewProps) => {
  const [fetchDatasetPreview, { loading, error, data }] = useDatasetPreviewLazyQuery({
    notifyOnNetworkStatusChange: true,
  });

  const loadDatasetPreview = useCallback(() => {
    fetchDatasetPreview({
      variables: {
        filter: {
          cubeName: cubeName,
          datasetId: datasetId,
        },
      },
    });
  }, [cubeName, datasetId, fetchDatasetPreview]);

  useEffect(() => {
    if (!cubeName || !datasetId) return;
    loadDatasetPreview();
  }, [cubeName, datasetId, loadDatasetPreview]);

  const tableData = useMemo(() => {
    return data?.datasetPreview?.dataset || [];
  }, [data]);

  const columns = useMemo(() => {
    if (tableData.length === 0) return [];
    const excludedColumns = ["DATASETID", "SOURCEFILEID", "CUBEID", "WASALREADYMERGEDFROMDATASETS", "FX_UNIQUE_ID"];
    const firstRow = tableData[0];
    return Object.keys(firstRow)
      .filter((key) => !excludedColumns.includes(key))
      .map((key, index) => {
        const validKey = key === "" || !key ? `Unnamed_Column_${index + 1}` : key;
        return {
          Header: validKey,
          accessor: validKey,
        };
      });
  }, [tableData]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({ columns, data: tableData });

  if (loading) {
    return (
      <StyledCard tableWidth={tableWidth}>
        <Box display="flex" justifyContent="center" alignItems="center" height="100px">
          <LoadingIcon sx={{ color: colors.grey[600] }} />
        </Box>
      </StyledCard>
    );
  }

  return (
    <StyledCard tableWidth={tableWidth}>
      <Typography variant="h6" fontWeight="medium" sx={{ color: colors.text.main, fontSize: "1.1rem", mb: 1 }}>
        Dataset Preview
      </Typography>
      <Divider
        sx={{
          margin: 0,
          marginBottom: "10px",
          borderColor: colors.grey[500],
          borderWidth: "1px",
        }}
      />
      <Typography variant="body1" sx={{ ...tableStyles.p, color: colors.text.main }}>
        Name: {datasetName}
      </Typography>
      <Typography variant="body1" sx={{ ...tableStyles.p, color: colors.text.main }}>
        Total count: {data?.datasetPreview?.totalCount ?? "N/A"}
      </Typography>

      {tableData.length > 0 ? (
        <Box sx={tableStyles.overflowContainer}>
          <TableContainer component={Paper} sx={{ boxShadow: "none", background: colors.background.default }}>
            <Table {...getTableProps()} size="small" aria-label="dataset preview table" sx={tableStyles.table}>
              <TableHead>
                {headerGroups.map((headerGroup, groupIndex) => (
                  <TableRow {...headerGroup.getHeaderGroupProps()} key={`header-group-${groupIndex}`}>
                    {headerGroup.headers.map((column, colIndex) => (
                      <TableCell
                        {...column.getHeaderProps()}
                        key={`header-${groupIndex}-${colIndex}`}
                        align="left"
                        sx={{
                          ...tableStyles.th,
                          maxWidth: "200px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <EllipsisWithTooltip placement="top">{column.render("Header")}</EllipsisWithTooltip>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableHead>
              <TableBody {...getTableBodyProps()}>
                {rows.map((row, rowIndex) => {
                  prepareRow(row);
                  return (
                    <TableRow {...row.getRowProps()} key={`row-${rowIndex}`}>
                      {row.cells.map((cell, cellIndex) => (
                        <TableCell
                          {...cell.getCellProps()}
                          key={`cell-${rowIndex}-${cellIndex}`}
                          align="left"
                          component="td"
                          sx={tableStyles.td}
                        >
                          {cell.render("Cell")}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ) : (
        <Box sx={{ ...tableStyles.noData, color: colors.text.secondary, textAlign: "center", mt: 2 }}>
          No data available
        </Box>
      )}
    </StyledCard>
  );
};

const StyledCard = styled(Box)<{ tableWidth?: string }>`
  width: ${({ tableWidth }) => tableWidth || "100%"};
  background: ${colors.background.default};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  padding: 1rem;
  border: 1px solid ${colors.grey[300]};
`;

export default DatasetPreviewComponent;
